import { AxiosRequestConfig, AxiosResponse } from 'axios'

import apiClient from '@core/api/client'
import { CreateZoneRequestDTO } from '@core/api/dtos/request/zone/create'
import { DeleteZoneRequestDTO } from '@core/api/dtos/request/zone/delete'
import { ExportZoneConnectionsRequest } from '@core/api/dtos/request/zone/exportZoneConnections'
import { UpdateZoneRequestDTO } from '@core/api/dtos/request/zone/update'
import { UpdateZonePlaylistsRequestDTO } from '@core/api/dtos/request/zone/updatePlaylists'
import { UpdateZoneSchedulesRequestDTO } from '@core/api/dtos/request/zone/updateSchedules'

import { RefreshZoneRequestDTO } from '../dtos/request/zone/refresh'
import { SendCodeEmailRequestDTO } from '../dtos/request/zone/sendCodeEmail'

export const zoneService = {
	/**
	 * Paginate account zones
	 */
	paginateZones(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/my/zones', config)
	},

	/**
	 * Get a zone by id
	 */
	getZone(
		playerId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/zones/${playerId}`, config)
	},

	/**
	 * Create a zone
	 */
	createZone(
		data: CreateZoneRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post('/my/zones', data, config)
	},

	/**
	 * Update a zone
	 */
	patchZone(
		zoneId: number,
		data: UpdateZoneRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.patch(`/my/zones/${zoneId}`, data, config)
	},

	/**
	 * Export zones as Excel file (.xlsx).
	 * Returns 200 with blob when the list is small enough for direct download,
	 * or 204 when the list is too large — in that case the file is queued and
	 * delivered to the user by email.
	 */
	exportZones(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/my/zones/export', {
			...config,
			responseType: 'blob',
			headers: {
				Accept:
					'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			},
		})
	},

	/**
	 * Get general connections
	 */
	getGeneralConnections(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/accounts/players/connections', config)
	},

	/**
	 * Export general connections as Excel file (.xlsx)
	 * This endpoint returns a blob with application/vnd.openxmlformats-officedocument.spreadsheetml.sheet content type
	 */
	exportGeneralConnections(
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(
			'/accounts/players/connections/download',
			{},
			{
				...config,
				responseType: 'blob',
				headers: {
					Accept:
						'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				},
			}
		)
	},

	/**
	 * Get zone connections
	 */
	getZoneConnections(
		uuid: string,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/accounts/players/${uuid}/connections`, config)
	},

	/**
	 * Export general connections as Excel file (.xlsx)
	 * This endpoint returns a blob with application/vnd.openxmlformats-officedocument.spreadsheetml.sheet content type
	 */
	exportZoneConnections(
		data: ExportZoneConnectionsRequest,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(
			`/accounts/players/${data.uuid}/connections/download`,
			data,
			{
				...config,
				responseType: 'blob',
				headers: {
					Accept:
						'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				},
			}
		)
	},

	/**
	 * Get zone occupation
	 */
	getZoneOccupation(
		accountId: number,
		zoneId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(
			`/accounts/${accountId}/players/${zoneId}/occupation`,
			config
		)
	},

	/**
	 * Get zone events
	 */
	getZoneEvents(
		zoneUUID: string,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(
			`/players/${zoneUUID}/events?event_names=playback_song,playback_advert`,
			config
		)
	},

	/**
	 * Delete a zone
	 */
	deleteZone(
		zoneId: number,
		data: DeleteZoneRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.delete(`/my/zones/${zoneId}`, {
			...config,
			data, // Send as request body for DELETE
		})
	},

	/**
	 * Validate name availability
	 */
	validateName(
		accountId: number,
		name: string,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/accounts/${accountId}/players/available`, {
			...config,
			params: {
				name,
			},
		})
	},

	/**
	 * Refresh a zone
	 */
	refresh(
		zoneUUID: string,
		data: RefreshZoneRequestDTO,
		config?: AxiosRequestConfig
	) {
		return apiClient.post(`/players/${zoneUUID}/refresh`, data, config)
	},

	/**
	 * Send code email
	 */
	sendCodeEmail(
		zoneID: number,
		data: SendCodeEmailRequestDTO,
		config?: AxiosRequestConfig
	) {
		return apiClient.post(`/accounts/players/${zoneID}/send_pin`, data, config)
	},

	/**
	 * Get the schedules of a zone
	 */
	getZoneSchedules(
		zoneId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/zones/${zoneId}/schedules`, config)
	},

	/**
	 * Update the schedules of a zone
	 */
	updateZoneSchedules(
		zoneId: number,
		data: UpdateZoneSchedulesRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.put(`/my/zones/${zoneId}/schedules`, data, config)
	},

	/**
	 * Get the onelink URL for a zone
	 */
	getOnelink(
		zoneId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/zones/${zoneId}/onelink`, config)
	},

	/**
	 * Add schedules to a zone
	 */
	addSchedulesToZone(
		zoneId: number,
		data: UpdateZoneSchedulesRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(`/my/zones/${zoneId}/schedules`, data, config)
	},

	/**
	 * Remove schedules from a zone
	 */
	removeSchedulesFromZone(
		zoneId: number,
		data: UpdateZoneSchedulesRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.delete(`/my/zones/${zoneId}/schedules`, {
			...config,
			data,
		})
	},

	/**
	 * Get the playlists directly assigned to a zone
	 */
	getZonePlaylists(
		zoneId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/zones/${zoneId}/playlists`, config)
	},

	/**
	 * Add playlists to a zone
	 */
	addPlaylistsToZone(
		zoneId: number,
		data: UpdateZonePlaylistsRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(`/my/zones/${zoneId}/playlists`, data, config)
	},

	/**
	 * Remove playlists from a zone
	 */
	removePlaylistsFromZone(
		zoneId: number,
		data: UpdateZonePlaylistsRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.delete(`/my/zones/${zoneId}/playlists`, {
			...config,
			data,
		})
	},
}

export default zoneService
